/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {
  AccountLink,
  GenericObject
} from '@ion/core/src/types/entities';
import {
  APIGatewaySmartSearch,
  Search,
  SearchCriteria
} from '@ion/core/src/types/services';
import {FilterChips} from '@ion/dls/src/types/editors';

export const SAVED_QUERIES_VALUE = 'savedQueries';

export type MenuItem = string | SearchQueryValue | DateQuery;

export interface Mapping {
  type: AttributeTypes;
  attributes: DefinedOptions;
  label: string;
  suggestion: SuggestionsType;
  options?: DefinedOptions[];
  key: string;
  suggestionsSource?: LookUpSource;
  suggestionsField?: string;
  isTreeView?: boolean;
  hasNoCharacterLimit?: boolean;
  hasPartialSubString?: boolean;
  hasSpecialCharacter?: boolean;
  hasNumericValue?: boolean;
  hasMultipleNumericValue?: boolean;
  useLookupOptionName?: boolean;
  localeValues?: {
    root?: string;
    children?: string;
  }
  hasFeatures?: GenericObject;
  hasFilePermission?: GenericObject;
  hasLargeMaxLimit?: boolean;
  hasAnyField?: boolean;
  hasCommaSeparated?: boolean;
  hasCollection?: boolean;
  hasIDMaxLimit?: boolean;
  hasExactMatch?: boolean;
  hasToolTip?: boolean;
  toolTipText?: string;
};

export interface Dropdown {
  open: any;
  type: SuggestionsType;
  sourceType: LookUpSource
}

export interface DefinedOptions {
  label: string;
  value: any;
}

export interface FormQueryType {
  name: string;
  condition: string;
  value: string;
}

export interface QuerySelection {
  attribute: string;
  option: MenuItem;
  source: SuggestionsType;
  query: string;
}

export interface QueryChipData {
  label: string;
  attribute: string;
  value?: any;
  disableDelete?: boolean;
  queryType?: SearchScopeValues | string;
  isExactQuery?: boolean;
  exactFields?: Partial<Record<SearchScopeValues, boolean>>;
}

export interface Item {
  name: string;
  id: number;
  subItems?: Item[];
  subItem?: Item[];
  searchableString?: string;
  isDeviceFiles?: boolean;
  displayTitle?: string;
  isTreeView?: boolean;
  children?: any;
}

export type DynamicDataItem = Item;
export type SearchQueryValue = Item;

export interface DynamicListData {
  loader: Function;
}

export interface LoaderProps {
  pageNumber: number;
  pageSize: number;
}

export interface PayloadTransformType {
  data: DynamicDataItem[];
}

export type DateQuery = {
  label: string;
  value: string[];
}

export interface SearchQuery {
  query: string;
  option?: MenuItem;
}

export interface LookUpData {
  id: number;
  name: string;
}

export enum LookUpSource {
  Group = 'Group',
  Device = 'Device',
  Account = 'Account',
  DeviceType = 'DeviceType',
  PegBehavior = 'PegBehavior',
  Application = 'Application',
  Role = 'Role',
  DeviceFiles = 'DeviceFiles',
  Categories = 'Categories',
  Architectures = 'Architectures',
  ManufacturingConfiguration = 'manufacturingConfiguration',
  ManufacturingCarrier = 'manufacturingCarrier',
  VehicleMake = 'vehicleMake',
  VehicleModel = 'vehicleModel'
}

export enum AttributeTypes {
  TEXT = 'TEXT',
  SELECT = 'SELECT',
  LOOKUP = 'LOOKUP',
  MULTISELECT = 'MULTISELECT',
  DATE = 'DATE'
}

export enum AttributeOperators {
  CONTAINS = 'CONTAINS',
  IN = 'IN',
  IS = 'IS',
  BETWEEN = 'BETWEEN'
};

/**
  * Status options
  */
export const NONE              = 'None';
export const ACTIVE            = 'Active';
export const OWNED             = 'Owned';
export const LEASED            = 'Leased';
export const RENTED            = 'Rented';
export const DISEL             = 'Diesel';
export const GASOLINE          = 'Gasoline';
export const CNG               = 'CNG';
export const LNG               = 'LNG';
export const ELECTRIC          = 'Electric';
export const HYDROGEN          = 'Hydrogen';
export const MILITARY          = 'Military';
export const EMERGENCY_VEHICLE = 'Emergency Vehicle';
export const AGRICULTURAL      = 'Agricultural';
export const MOTOR_HOME        = 'Motor Home';
export const OTHER             = 'Other';
export const USA               = 'USA';
export const MEXICO            = 'Mexico';
export const CANADA            = 'Canada';

export const ENABLED    = 'Enabled';
export const DISABLED   = 'Disabled';
export const SUSPENDED  = 'Suspended';
export const DELETED    = 'Deleted';
export const VALID      = 'Valid';
export const INVALID    = 'Invalid';
export const PENDING    = `${'Pending, Created'}`;
export const CANCELLED  = `${'InProgressCancelled, Cancelled, Cancelling'}`;
export const INPROGRESS = `${'InProgress, Validating, Validated'}`;
export const COMPLETED  = 'Completed';
export const FAILED     = 'Failed';
export const SELECTALL  = 'Select All';
export const ON         = 'true';
export const OFF        = 'false';

/**
  * Yes/No options
  */
export const YES = 'Yes';
export const NO  = 'NO';

/**
  * Dropdown selection type: It can be either Attribute or Value
  */
export enum SelectionType {
  ATTRIBUTE = 'ATTRIBUTE',
  VALUE = 'VALUE'
}

/**
  * Dropdown types
  */
export enum SuggestionsType {
  ATTRIBUTE = 'ATTRIBUTE',
  LOOKUP = 'LOOKUP',
  TEXT = 'TEXT',
  SELECTION = 'SELECTION',
  MULTISELECTION = 'MULTISELECTION',
  DATE = 'DATE'
}

/**
 * PEG Labels
 */

export enum PegLabels {
  ACCUMULATOR = 'ACCUMULATOR',
  EVENT = 'EVENT'
}

/**
 * Application Ids
 */
export enum ApplicationIds {
  MANUFACTURER = 'manufacturer',
  MICRO_PROCESSOR = 'microprocessor',
  MODEL = 'model',
  STATUS = 'status',
  CELLULAR_MODEM = 'cellularModem',
  DEVICE_FAMILY = 'deviceFamily',
  CARRIER = 'carrier'
}

/**
 * Device Part Numbers
 */
export enum DevicePartNumbers {
  DEVICE_PART_NUM = 'devicePartNum',
  DEVICE_TYPE = 'deviceType',
  DEVICE_CONFIGURATION_NAME = 'deviceConfigurationName',
  CARRIER = 'carrier',
  IS_THIRD_PARTY = 'isThirdParty'
}

/**
 * Device Family
 */
export enum DeviceFamily {
  BLE = 'BLE',
  LMUTTU = 'LMU_TTU',
  ROUTERFUSION = 'ROUTER_FUSION',
  ROUTERVANGUARD = 'ROUTER_VANGUARD'
}

/**
 * Query Names
 */
export enum copyPasteQueryNames {
  APPID = 'appId',
  DEVICES = 'devices',
  ESN = 'esn',
  SERIALNUMBER = 'serialNumber',
  ANY = 'any'
};

/**
 * Date range values
 */
export enum SearchDateRange {
  NULL = null,
  ONE = 1,
  THREE = 3,
  SEVEN = 7,
  FOURTEEN = 14
}

export const DateAttributes = [
  'idReportTimestamp'
];

export const DateLabels = [
  'Last 7 days',
  'Last 3 days',
  'Last 1 day'
];

/**
 * Component Props
 */
export interface SearchSuggestionsProps {
  query: string[];
  listOptions: string[],
  attributeMappings: {[key: string]: Mapping};
  onMenuSelection: Function;
  menuAutoFocus: boolean;
  menuSelectionType: SuggestionsType;
  selectedSuggestions: string[];
  menuListRef: any;
  lookUp: DynamicListData;
  inputRef?: React.RefObject<HTMLInputElement>;
  closeDropdown: Function;
  inputEventTrigger?: Function;
  updateFilterLookUpData?: Function;
  inputValue?: string;
  isCaseInsensitive?: boolean;
  accountTree: AccountIdLinkMap;
  setShowSuggestions: Function;
  hasDateField?: boolean;
}

export type AccountIdLinkMap = {
  [accountId: number]: AccountLink;
}

export interface BaseSuggestionProps {
  onSelection: Function;
  classes: any;
  listOptions: string[],
}

export interface AttributeSuggestionProps {
  onSelection: (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => void;
  mapping: {[key: string]: Mapping};
  value: string;
  classes?: any;
  listOptions?: string[],
  hasSavedQueries?: boolean;
}

export interface MultiSuggestionProps extends BaseSuggestionProps {
  selectedSuggestions: string[];
  isMultiSelection: boolean;
  menuSelectionType?: SuggestionsType;
  setShowSuggestions?: Function;
  hasDateField?: boolean;
}

export interface CustomDateRangeProps {
  onSelection: Function;
  menuItemClass: string;
  menuSubmissionWrapperClass: string;
  setShowSuggestions?: Function;
  includeContainerStyles?: boolean;
}

export interface IDatePickerState {
  open: boolean;
  value: any;
}

export interface GetSelectedQueryType {
  menuItem: MenuItem;
  source: SuggestionsType;
  query: string;
  attribute: string;
}

export type Mappings = {[key: string]: Mapping};
export type UniqueConditions = string;
export type SearchFilters = GenericObject;

export interface SelectAllSearch {
  includeSubAccounts?: boolean;
  areTotalRowsSelected?: boolean;
  search?: Search | SearchCriteria | APIGatewaySmartSearch;
  filters?: FilterChips;
  totalRowsCount?: number;
}

export type SearchScopeMatch = Partial<Record<SearchScopeValues, boolean>>;

export enum SearchScopeValues {
  FIRMWARE_VERSION = 'firmwareVersion',
  CONFIG_VERSION = 'configVersion',
  DEVICE_FILES = 'provisioningDevicefiles',
  FILE_VERSION = 'version',
  APP_ID = 'appId',
  DEVICE_NAME = 'name',
  DEVICES = 'devices',
  EXACT = 'exact',
  PARTIAL = 'partial',
  JOBS = 'jobs',
  JOB_ID = 'jobId',
  NAME = 'name',
  CREATOR = 'creator',
  ESN = 'esn',
  SERIAL_NUMBER = 'serialNumber',
  IMEI = 'imei',
  IMEI2 = 'imei2',
  AIR_ID = 'airId',
  ICCID = 'iccid',
  MEID = 'meid',
  MDT = 'mdt',
  LMU = 'lmu',
  USER_ESN_MOBILE_ID = 'userESN',
  ANDROID_OS_VERSION = 'androidOSVersion',
  VIN = 'vin',
  VEHICLE_DISCOVERY = 'vehicle-discovery-page'
};

export enum InvalidDateErrorType {
  EMPTY_DATES = 'EMPTY_DATES',
  EMPTY_START_DATE = 'EMPTY_START_DATE',
  EMPTY_END_DATE = 'EMPTY_END_DATE',
  INVALID_DATES = 'INVALID_DATES'
}

export enum SearchSentinelTagType {
  PRIMARY = 'Primary Device',
  SECONDARY = 'Secondary Device',
  ALL = 'All Sentinel Devices'
};
